import { useEffect } from 'react';
import { useTheme } from '@mui/material/styles';

/**
 * Bridges MUI theme tokens → CSS custom properties on <body>.
 * This allows existing CSS files (and CSS animations) to keep using
 * var(--el-*) while we incrementally migrate components to MUI sx prop.
 * Once all components use sx/useTheme(), this bridge can be removed.
 */
export default function CssVariableBridge() {
  const theme = useTheme();

  useEffect(() => {
    const s = document.body.style;
    const p = theme.palette;
    const t = theme.typography;
    const sh = theme.shape;

    // Brand Colors
    s.setProperty('--el-brand-cyan', p.primary.main);
    s.setProperty('--el-brand-cyan-hover', p.primary.dark);
    s.setProperty('--el-brand-cyan-light', p.primary.light);
    s.setProperty('--el-brand-secondary', p.secondary.main);
    s.setProperty('--el-brand-dark-blue', p.secondary.dark);

    // Backgrounds
    s.setProperty('--el-bg-primary', p.background.default);
    s.setProperty('--el-bg-surface', p.background.surface || p.background.default);
    s.setProperty('--el-bg-dialog', p.background.paper);
    s.setProperty('--el-bg-navy-mid', p.brand?.navyMid || p.background.default);
    s.setProperty('--el-bg-body-gradient', p.background.gradient || p.background.default);
    s.setProperty('--el-bg-white', p.grey[50] === '#f3f3f3' ? '#ffffff' : (p.common?.white || '#ffffff'));
    s.setProperty('--el-bg-light', p.grey[100] || '#f1f1f1');
    s.setProperty('--el-bg-light-alt', p.grey[50] || '#f3f3f3');

    // Text Colors
    s.setProperty('--el-text-primary', p.text.primary);
    s.setProperty('--el-text-secondary', p.text.secondary);
    s.setProperty('--el-text-muted', p.text.disabled);
    s.setProperty('--el-text-hint', p.text.hint || p.text.disabled);
    s.setProperty('--el-text-dark', p.text.dark || '#000000');
    s.setProperty('--el-text-body', p.text.body || '#4a4a4a');
    s.setProperty('--el-text-heading', p.text.heading || '#2a2a2a');
    s.setProperty('--el-text-dark-gray', p.grey[800] || '#626262');
    s.setProperty('--el-text-icon-gray', p.grey[700] || '#6a6a6a');

    // Status Colors
    s.setProperty('--el-success', p.success.main);
    s.setProperty('--el-success-bg', p.success.light);
    s.setProperty('--el-success-text', p.success.dark);
    s.setProperty('--el-success-border', p.success.border || p.success.light);
    s.setProperty('--el-error', p.error.main);
    s.setProperty('--el-error-hover', p.error.dark || p.error.main);
    s.setProperty('--el-status-green', p.success.main);
    s.setProperty('--el-status-red', p.error.main);

    // Border Colors
    s.setProperty('--el-border-light', p.border?.light || p.grey[300]);
    s.setProperty('--el-border-medium', p.border?.medium || p.grey[400]);
    s.setProperty('--el-border-dark', p.border?.dark || p.grey[500]);
    s.setProperty('--el-border-input', p.border?.input || p.grey[700]);
    s.setProperty('--el-border-muted', p.border?.muted || p.grey[400]);
    s.setProperty('--el-border-table', p.border?.tableCell || p.divider);
    s.setProperty('--el-border-on-dark', p.border?.onDark || 'rgba(255,255,255,0.06)');
    s.setProperty('--el-border-on-dark-alt', p.border?.onDarkAlt || 'rgba(255,255,255,0.08)');
    s.setProperty('--el-border-subtle', p.grey[300] || '#d6d6d6');

    // Grey Scale
    s.setProperty('--el-grey-50', p.grey[50]);
    s.setProperty('--el-grey-100', p.grey[100]);
    s.setProperty('--el-grey-200', p.grey[200]);
    s.setProperty('--el-grey-300', p.grey[300]);
    s.setProperty('--el-grey-400', p.grey[400]);
    s.setProperty('--el-grey-500', p.grey[500]);
    s.setProperty('--el-grey-600', p.grey[600]);
    s.setProperty('--el-grey-700', p.grey[700]);
    s.setProperty('--el-grey-800', p.grey[800]);
    s.setProperty('--el-grey-900', p.grey[900]);

    // Gradients
    s.setProperty('--el-gradient-avatar', p.gradient?.avatar || `linear-gradient(135deg, ${p.primary.main} 0%, ${p.primary.dark} 100%)`);
    s.setProperty('--el-gradient-voice-btn', p.gradient?.voiceButton || `linear-gradient(135deg, ${p.grey[100]} 0%, ${p.grey[200]} 100%)`);
    s.setProperty('--el-gradient-voice-tooltip', p.gradient?.voiceTooltip || `linear-gradient(135deg, ${p.primary.main} 0%, ${p.primary.light} 100%)`);
    s.setProperty('--el-gradient-voice-overlay', p.voice?.overlayBg || `linear-gradient(180deg, ${p.background.default} 0%, ${p.background.default} 100%)`);

    // Voice State Colors
    s.setProperty('--el-voice-init-1', p.voice?.initBg1 || '#e0f7fa');
    s.setProperty('--el-voice-init-2', p.voice?.initBg2 || '#b2ebf2');
    s.setProperty('--el-voice-init-3', p.voice?.initBg3 || '#80deea');
    s.setProperty('--el-voice-think-1', p.voice?.thinkBg1 || '#fff8e1');
    s.setProperty('--el-voice-think-2', p.voice?.thinkBg2 || '#ffecb3');
    s.setProperty('--el-voice-think-3', p.voice?.thinkBg3 || '#ffe082');
    s.setProperty('--el-voice-speak-1', p.voice?.speakBg1 || '#e8f5e9');
    s.setProperty('--el-voice-speak-2', p.voice?.speakBg2 || '#c8e6c9');
    s.setProperty('--el-voice-speak-3', p.voice?.speakBg3 || '#a5d6a7');

    // Shadows
    s.setProperty('--el-shadow-subtle', theme.shadows[1]);
    s.setProperty('--el-shadow-medium', theme.shadows[2]);
    s.setProperty('--el-shadow-light', theme.shadows[3]);
    s.setProperty('--el-shadow-drop', theme.shadows[4]);
    s.setProperty('--el-shadow-elevated', theme.shadows[5]);
    s.setProperty('--el-shadow-soft', theme.shadows[6]);
    s.setProperty('--el-shadow-error', theme.shadows[7]);
    s.setProperty('--el-shadow-error-hover', theme.shadows[8]);
    s.setProperty('--el-shadow-blob-outer', theme.shadows[9]);
    s.setProperty('--el-shadow-blob-outer-lg', theme.shadows[10]);
    s.setProperty('--el-shadow-blob-inset', theme.shadows[11]);

    // Typography
    s.setProperty('--el-font-family', t.fontFamily);
    s.setProperty('--el-font-size-xs', '10px');
    s.setProperty('--el-font-size-sm', t.overline?.fontSize || '11px');
    s.setProperty('--el-font-size-caption', t.caption?.fontSize || '12px');
    s.setProperty('--el-font-size-body2', t.body2?.fontSize || '13px');
    s.setProperty('--el-font-size-body1', t.body1?.fontSize || '14px');
    s.setProperty('--el-font-size-button', t.button?.fontSize || '15px');
    s.setProperty('--el-font-size-h6', t.h6?.fontSize || '16px');
    s.setProperty('--el-font-size-h5', t.h5?.fontSize || '18px');
    s.setProperty('--el-font-size-h4', t.h4?.fontSize || '20px');
    s.setProperty('--el-font-size-h3', t.h3?.fontSize || '22px');
    s.setProperty('--el-font-size-h2', t.h2?.fontSize || '24px');
    s.setProperty('--el-font-size-title', t.h1?.fontSize || '50px');

    // Spacing
    const sp = theme.spacing(1).replace('px', '');
    s.setProperty('--el-space-xs', theme.spacing(0.5));
    s.setProperty('--el-space-sm', `${Math.round(sp * 0.625)}px`);
    s.setProperty('--el-space-md', theme.spacing(1));
    s.setProperty('--el-space-base', `${Math.round(sp * 1.25)}px`);
    s.setProperty('--el-space-lg', theme.spacing(1.5));
    s.setProperty('--el-space-xl', `${Math.round(sp * 1.875)}px`);
    s.setProperty('--el-space-2xl', theme.spacing(2));
    s.setProperty('--el-space-3xl', theme.spacing(2.5));
    s.setProperty('--el-space-4xl', theme.spacing(3.75));

    // Border Radius
    s.setProperty('--el-radius-xs', `${sh.radiusSmall || 4}px`);
    s.setProperty('--el-radius-sm', `${sh.radiusSmall + 1 || 5}px`);
    s.setProperty('--el-radius-md', `${sh.radiusMedium || 6}px`);
    s.setProperty('--el-radius-base', `${sh.borderRadius}px`);
    s.setProperty('--el-radius-lg', `${sh.radiusLarge || 10}px`);
    s.setProperty('--el-radius-xl', `${sh.radiusXl || 12}px`);
    s.setProperty('--el-radius-2xl', `${sh.radiusXxl || 16}px`);
    s.setProperty('--el-radius-pill', `${sh.radiusPill || 22}px`);
    s.setProperty('--el-radius-circle', '50%');

    // Layout
    s.setProperty('--el-expandable-width', '800px');
    s.setProperty('--el-user-mgmt-max-width', '860px');
    s.setProperty('--el-chat-widget-width', '450px');
  }, [theme]);

  return null;
}
